import { Skeleton, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery } from '~/packages/borrowing-base/requests/portfolioCovenants.gql.generated';
import {
  errorToastConfig,
  getDefaultDashboardDisplayEndDate,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { CovenantChartTemplate } from '~/packages/portfolio/ui/covenants/CovenantChartTemplate';
import { createCovenantsChartConfig } from '~/packages/portfolio/ui/covenants/CovenantsContainer';
import {
  covenantsInitialStartDate,
  defaultPortfolioCovenantValueType,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';

type DigitalMonthlyReportSinglePortfolioCovenantDetailProps = {
  creditFacilityId: string;
  creditAgreementPortfolioCovenants: CreditAgreement.DomainEntity.PortfolioCovenantProperty[];
  covenantKey: string;
};
export const DigitalMonthlyReportSinglePortfolioCovenantDetail = ({
  creditFacilityId,
  creditAgreementPortfolioCovenants,
  covenantKey,
}: DigitalMonthlyReportSinglePortfolioCovenantDetailProps) => {
  const toast = useToast();

  const {
    loading: isPortfolioCovenantsLoading,
    data: allPortfolioCovenantsData,
    refetch: refetchSuccessPortfolioCovenants,
  } = useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      mode: 'RESULT',
      startDate: covenantsInitialStartDate,
      endDate: getDefaultDashboardDisplayEndDate(),
    },
  });

  // it's possible for the covenantKey of a portfolio covenant calculation to no longer exist in the current CA, if it's been deleted
  // we will still show the calculations and graphs as historical data, but will not be able to parse out the thresholdType (min vs max) later downstream, which is fine
  const creditAgreementPortfolioCovenant:
    | CreditAgreement.DomainEntity.PortfolioCovenantProperty
    | undefined = useMemo(() => {
    return _.find(
      creditAgreementPortfolioCovenants,
      (portfolioCovenant) => portfolioCovenant.covenantKey === covenantKey,
    );
  }, [covenantKey, creditAgreementPortfolioCovenants]);

  if (isPortfolioCovenantsLoading || !allPortfolioCovenantsData) {
    return (
      <Skeleton
        height="600px"
        data-testid="portfolio-covenant-results-loading"
      />
    );
  }

  const valueType =
    creditAgreementPortfolioCovenant?._config_?.valueType ??
    defaultPortfolioCovenantValueType;

  return (
    <CovenantChartTemplate
      covenantType="portfolio"
      creditFacilityId={creditFacilityId}
      chartTitle={commonUtils.toTitleCase(covenantKey)}
      covenantKey={covenantKey}
      chartConfig={createCovenantsChartConfig(valueType)}
      key={`portfolio-covenant-chart-${covenantKey}`}
      creditAgreementCovenant={creditAgreementPortfolioCovenant}
      covenantData={
        allPortfolioCovenantsData.getSuccessPortfolioCovenantsByCreditFacilityId
      }
      refetchCovenants={refetchSuccessPortfolioCovenants}
      valueType={valueType}
    />
  );
};
